import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  SendIcon,
  UndoDotIcon,
  UsersIcon,
  XCircleIcon,
  XIcon
} from 'lucide-react';
import * as api from '../../api/research';
import type { ReviewerRow, WorkItemRow } from '../../api/types';
import { ObservationLetterField, observationLetterProblem } from './ObservationLetterField';

/**
 * Does the one thing a queued proposal is waiting for, without leaving the
 * dashboard. Which actions appear comes from the proposal's status and the
 * office asking — the same rules the detail page uses, and the same rules the
 * stored procedures enforce, so an action offered here is one the server will
 * actually accept.
 */

interface ActionPanelProps {
  item: WorkItemRow;
  sessionToken: string;
  isSrcAdmin: boolean;
  isIrbAdmin: boolean;
  onClose: () => void;
  /** Called after anything that changed the proposal, so the queue can reload. */
  onDone: () => void;
  onOpenProposal: () => void;
}

export function ActionPanel({
  item,
  sessionToken,
  isSrcAdmin,
  isIrbAdmin,
  onClose,
  onDone,
  onOpenProposal
}: ActionPanelProps) {
  const [remarks, setRemarks] = useState('');
  const [letter, setLetter] = useState<File | null>(null);
  const [letterKey, setLetterKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [reviewers, setReviewers] = useState<ReviewerRow[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [loadingReviewers, setLoadingReviewers] = useState(false);

  const status = item.StatusId;
  const canForward =
    (isIrbAdmin && ['04', '06', '08'].includes(status)) || (isSrcAdmin && status === '02');
  const canSendBack =
    (isIrbAdmin && ['04', '05', '06', '08', '09'].includes(status)) ||
    (isSrcAdmin && status === '02');
  const canDecide = isIrbAdmin && ['04', '06', '08', '09'].includes(status);
  const canAssign = isIrbAdmin && ['04', '05', '06', '08'].includes(status);
  /* Exempt review needs no reviewers: once cleared by the SRC (04), or
     re-submitted (08), the IRB office can approve it directly. */
  const exemptReady =
    isIrbAdmin && ['04', '08'].includes(status) && item.ReviewCategory === 'Exempted';

  const loadReviewers = useCallback(async () => {
    if (!canAssign) return;
    setLoadingReviewers(true);
    try {
      const res = await api.listReviewers(sessionToken, item.ResearchCode);
      if (res.ok) {
        setReviewers(res.rows);
        setPicked(res.rows.filter((r) => r.IsAssigned).map((r) => r.ReviewerId));
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the reviewers.');
    } finally {
      setLoadingReviewers(false);
    }
  }, [canAssign, item.ResearchCode, sessionToken]);

  useEffect(() => {
    void loadReviewers();
  }, [loadReviewers]);

  /* Escape closes, as it would for any dialog. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const act = async (fn: () => Promise<{ ok: boolean; message: string }>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      if (res.ok) {
        setNotice(res.message);
        setRemarks('');
        setLetter(null);
        setLetterKey((k) => k + 1);
        onDone();
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const newlyPicked = picked.filter(
    (id) => !reviewers.find((r) => r.ReviewerId === id)?.IsAssigned
  );

  return (
    <div className="panel-backdrop" role="dialog" aria-modal="true" aria-label="Act on this proposal">
      <aside className="panel">
        <header className="panel-head">
          <div>
            <span className="panel-eyebrow">
              {item.IrbNumber ?? `#${item.ResearchCode}`} · {item.StatusDescription}
            </span>
            <h2 className="panel-title">{item.Title || 'Untitled proposal'}</h2>
            <p className="panel-sub">
              {item.PiName || item.ResearcherName}
              {item.DaysWaiting > 0 ? ` · waiting ${item.DaysWaiting} day${item.DaysWaiting === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          <button type="button" className="btn-icon" aria-label="Close" onClick={onClose}>
            <XIcon size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="panel-body">
          {error ? (
            <div className="notice notice--error" role="alert">
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {notice ? (
            <div className="notice" role="status">
              <CheckCircle2Icon size={18} aria-hidden="true" />
              <span>{notice}</span>
            </div>
          ) : null}

          <button type="button" className="btn btn--secondary btn--block" onClick={onOpenProposal}>
            Read the full proposal
          </button>

          {canAssign ? (
            <section style={{ marginTop: 'var(--space-5)' }}>
              <h3 className="review-block-title">Reviewers</h3>
              {loadingReviewers ? (
                <p className="muted">Loading the reviewer list…</p>
              ) : reviewers.length === 0 ? (
                <p className="muted">No reviewer accounts found.</p>
              ) : (
                <>
                  <ul className="picker">
                    {reviewers.map((r) => (
                      <li key={r.ReviewerId}>
                        <label className="checkbox-inline">
                          <input
                            type="checkbox"
                            checked={picked.includes(r.ReviewerId)}
                            disabled={r.IsAssigned}
                            onChange={() =>
                              setPicked((prev) =>
                                prev.includes(r.ReviewerId)
                                  ? prev.filter((id) => id !== r.ReviewerId)
                                  : [...prev, r.ReviewerId]
                              )
                            }
                          />
                          <span>
                            {r.Name}
                            <span className="muted">
                              {r.IsAssigned ? ' · already assigned' : ` · ${r.OpenReviews} open`}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>

                  <div className="field" style={{ marginTop: 'var(--space-3)', maxWidth: 220 }}>
                    <label className="field-label" htmlFor="panel-due">
                      Reviews due by
                    </label>
                    <input
                      id="panel-due"
                      className="input"
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn btn--primary"
                    style={{ marginTop: 'var(--space-3)' }}
                    disabled={busy || newlyPicked.length === 0}
                    onClick={() =>
                      void act(async () => {
                        const res = await api.assignReviewers({
                          SessionToken: sessionToken,
                          ResearchCode: item.ResearchCode,
                          ReviewerIds: JSON.stringify(picked),
                          DueDate: dueDate
                        });
                        if (res.ok) await loadReviewers();
                        return res;
                      })
                    }
                  >
                    <UsersIcon size={16} aria-hidden="true" />
                    {busy
                      ? 'Assigning…'
                      : newlyPicked.length === 0
                        ? 'Select a reviewer to assign'
                        : `Assign ${newlyPicked.length} reviewer${newlyPicked.length === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
            </section>
          ) : null}

          {canForward || canSendBack || canDecide ? (
            <section style={{ marginTop: 'var(--space-5)' }}>
              <h3 className="review-block-title">Decision</h3>
              {exemptReady ? (
                <div className="notice" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <CheckCircle2Icon size={18} aria-hidden="true" />
                  <span>
                    Exempt review: no reviewers are needed. You can approve this proposal
                    directly, or send it back.
                  </span>
                </div>
              ) : null}
              <div className="field">
                <label className="field-label" htmlFor="panel-remarks">
                  Remarks
                </label>
                <p className="field-hint">
                  Emailed to the researcher and kept in the proposal's history. Required
                  when sending back or refusing.
                </p>
                <textarea
                  id="panel-remarks"
                  className="textarea"
                  rows={4}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              {canSendBack ? (
                <ObservationLetterField
                  id="panel-observation"
                  file={letter}
                  onChange={setLetter}
                  resetKey={letterKey}
                />
              ) : null}

              <div className="inline-actions" style={{ marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
                {canForward ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={busy}
                    onClick={() =>
                      void act(() =>
                        api.forwardProposal({
                          SessionToken: sessionToken,
                          ResearchCode: item.ResearchCode,
                          Remarks: remarks
                        })
                      )
                    }
                  >
                    <SendIcon size={16} aria-hidden="true" />
                    Forward
                  </button>
                ) : null}

                {canSendBack ? (
                  <button
                    type="button"
                    className="btn btn--secondary"
                    disabled={busy}
                    onClick={() =>
                      void act(async () => {
                        const problem = !remarks.trim()
                          ? 'Please enter the corrections.'
                          : observationLetterProblem(letter);
                        if (problem || !letter) return { ok: false, message: problem ?? '' };
                        return api.sendBackProposal({
                          SessionToken: sessionToken,
                          ResearchCode: item.ResearchCode,
                          Remarks: remarks,
                          ObservationLetter: letter
                        });
                      })
                    }
                  >
                    <UndoDotIcon size={16} aria-hidden="true" />
                    Send back
                  </button>
                ) : null}

                {canDecide ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--success"
                      disabled={busy}
                      onClick={() =>
                        void act(() =>
                          api.decideProposal({
                            SessionToken: sessionToken,
                            ResearchCode: item.ResearchCode,
                            Approved: true,
                            Remarks: remarks
                          })
                        )
                      }
                    >
                      <CheckCircle2Icon size={16} aria-hidden="true" />
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={busy}
                      onClick={() =>
                        void act(() =>
                          api.decideProposal({
                            SessionToken: sessionToken,
                            ResearchCode: item.ResearchCode,
                            Approved: false,
                            Remarks: remarks
                          })
                        )
                      }
                    >
                      <XCircleIcon size={16} aria-hidden="true" />
                      Do not approve
                    </button>
                  </>
                ) : null}
              </div>
            </section>
          ) : (
            <p className="muted" style={{ marginTop: 'var(--space-5)' }}>
              Nothing is open to your office at this stage.
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}
